import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    question: 'What is the Kiaan Agrowtech farming partnership?',
    answer:
      "Kiaan Agrowtech's farming partnership is a group farming model where like-minded individuals come together to grow bananas on professionally managed farmland. You contribute toward a farming cycle, Kiaan handles all on-ground operations — planting, irrigation, AI-assisted disease management, and harvest — and the net proceeds from the harvest are shared between Kiaan and farming partners.\n\nThis is pure agriculture — not a financial investment scheme or securities product.",
  },
  {
    question: 'What do I actually get as a farming partner?',
    answer:
      'As a farming partner you get a 50% share of net harvest proceeds from your assigned project, full transparency via the Kiaan app – FarmTogether (MIS reports, farm photos, harvest data, P&L), and a partnership agreement signed digitally. You are not purchasing land or a financial instrument — you are participating in a banana farming cycle as a partner.',
  },
  {
    question: 'How is each farm project structured?',
    answer:
      'Each farming project covers 10 acres and is assigned a unique project code (e.g. MYSBN06). Multiple farming partners can be mapped to the same project. All MIS reports, harvest data, and P&L shared with you are specific to your project code. Kiaan leases the land, manages all operations, and delivers harvest proceeds to partners at the end of each cycle — typically 12 to 14 months per cycle.',
  },
  {
    question: 'What banana varieties does Kiaan grow?',
    answer:
      'Kiaan grows Yelakki and G9 Grand Naine bananas — two complementary varieties. Yelakki commands strong local market prices in South India. G9 delivers high yield volumes suited to commercial trade. Planting both varieties reduces risk from price fluctuations and seasonal demand shifts.',
  },
  {
    question: 'How much do I need to contribute to join?',
    answer:
      'The farming contribution is structured on a per-acre basis. Specific contribution amounts are shared during the partner onboarding discussion — contact Kiaan directly for current project details.\n\nContribution covers: land lease, saplings & infrastructure, manure, nutrients & labour, and maintenance & miscellaneous costs for a full crop cycle.',
  },
  {
    question: 'What is the payment schedule for my contribution?',
    answer:
      'Your contribution is structured across three stages, aligned with the farming cycle\'s key milestones:\n\n• 10% — At time of joining: Advance to confirm partnership and reserve project allocation.\n• 70% — After agreement signed: Major contribution paid once partnership documents are executed — enables land prep and planting to begin.\n• 20% — 3 months after project start: Final tranche covering inputs and operations for the active growing phase.',
  },
  {
    question: 'When will I get my principal contribution back?',
    answer:
      'The farming partnership operates on a 3-year lock-in period:\n\n• Year 1 (Cycle 1 harvest): You receive your 50% share of net harvest proceeds. Principal remains in the partnership. (Profit only)\n• Year 2 (Cycle 2 harvest): You again receive your 50% share. Principal continues in the partnership. (Profit only)\n• End of Year 3 (Full exit): You receive your principal contribution back along with your 50% share of the third cycle\'s harvest proceeds. (Principal + profit)\n\nEach crop cycle runs approximately 12–14 months. The lock-in ensures continuity across three full growing cycles.',
  },
  {
    question: 'How are harvest proceeds calculated and shared?',
    answer:
      'After the harvest is sold, total revenue is calculated. Production costs for that cycle are deducted to arrive at net proceeds. The net proceeds are then split 50% to Kiaan Agrowtech and 50% to farming partners, proportional to each partner\'s contribution share. Full P&L documentation is made available to every partner via the app (FarmTogether) after each harvest.',
  },
  {
    question: 'Are returns guaranteed?',
    answer:
      'No. This is farming — harvest proceeds depend on actual yield, market prices, and farm performance, all of which vary by season. Kiaan does not guarantee specific returns and does not treat this as a financial product.\n\nWhat Kiaan does offer is full transparency, experienced farm management built over three production cycles, and a disciplined approach to risk management across six categories: farm selection, pest & disease, climate & water, soil health, skilled labour, and market access.',
  },
  {
    question: 'What happens to my contribution if the crop fails?',
    answer:
      "Farming carries natural risk, and no crop cycle is fully insulated from weather, market, or disease challenges. Kiaan's risk management practices — including AI-assisted disease diagnosis, certified planting material, drip irrigation, and diversified varieties — are designed to minimise these risks. In a poor harvest cycle, net proceeds may be lower or nil. Partners share in the outcome of the actual harvest. Kiaan maintains full transparency through MIS reports throughout the cycle.",
  },
  {
    question: 'How does Kiaan use technology in farming?',
    answer:
      'Kiaan uses AI-powered disease diagnosis to identify and treat crop diseases with precision. Recently, AI was used to correctly differentiate between Fusarium Wilt (fungal) and bacterial corm rot — enabling targeted treatment that saved an entire crop batch, something a standard consultant missed. Kiaan also uses drip irrigation systems with fertigation scheduling, real-time farm monitoring, and data-driven input management to optimise yield and minimise waste.',
  },
  {
    question: "How will I stay updated on my farm's progress?",
    answer:
      'Through the Kiaan app (FarmTogether) — available on iOS and Android. The app gives you access to your project dashboard, live milestone tracking, farm photo updates, monthly MIS reports, harvest details, P&L statements, and direct messaging with the Kiaan team. You receive push notifications every time something significant is updated.',
  },
  {
    question: 'Can I visit the farm?',
    answer:
      'Yes. Kiaan welcomes farming partners to visit their project farm. Farm visits can be arranged by contacting the Kiaan team in advance. Seeing your farm firsthand is something we actively encourage.',
  },
  {
    question: 'Is my harvest share taxable?',
    answer:
      'Under the Income Tax Act, 1961, agricultural income earned by a farming partnership entity may be exempt under Section 10(1). Additionally, a partner\'s share of profit from a partnership firm is exempt in the hands of the partner under Section 10(2A).\n\nTerms and conditions apply. Consult your CA for individual tax advice — tax treatment may vary based on your residential status and applicable rules.',
  },
  {
    question: 'What documents will I need to sign?',
    answer:
      'You will be required to sign a farming partnership agreement that outlines your contribution, the project you are mapped to, the profit-sharing structure, and the terms of the partnership. All documents are signed digitally through the Kiaan app.',
  },
];

// Accordion item — full-width question bar with dropdown answer
const FAQItem = ({ faq, index, isOpen, onToggle }) => (
  <motion.div
    className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
      isOpen
        ? 'border-green-200 bg-white shadow-[0_14px_40px_-20px_rgba(2,58,16,0.35)]'
        : 'border-green-100 bg-white/70 hover:border-green-200 hover:shadow-sm'
    }`}
    initial={{ opacity: 0, y: 15 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.04 }}
    viewport={{ once: true }}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 py-4 md:py-5 px-5 md:px-6 text-left"
    >
      <span
        className={`text-sm md:text-base font-semibold pr-2 transition-colors duration-200 ${
          isOpen ? 'text-[rgb(2,58,16)]' : 'text-gray-800'
        }`}
      >
        {faq.question}
      </span>
      <motion.span
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
          isOpen
            ? 'bg-gradient-to-br from-[rgb(2,58,16)] to-green-700 text-white'
            : 'bg-green-100 text-green-700'
        }`}
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.2 }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="w-4 h-4"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="px-5 md:px-6 pb-5">
            <div className="border-t border-green-100 pt-4">
              <p className="text-sm md:text-[15px] text-gray-600 font-serif leading-relaxed whitespace-pre-line">
                {faq.answer}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const sectionRef = useRef(null);

  // Show questions up to (and including) "Are returns guaranteed?" by default
  const returnsIndex = faqs.findIndex((f) => f.question === 'Are returns guaranteed?');
  const initialCount = returnsIndex >= 0 ? returnsIndex + 1 : faqs.length;
  const visibleFaqs = showAll ? faqs : faqs.slice(0, initialCount);

  const handleToggle = () => {
    setShowAll((prev) => {
      const next = !prev;
      // When collapsing, scroll back so the section shows properly
      if (!next && sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return next;
    });
  };

  return (
    <section ref={sectionRef} className="py-12 md:py-16 bg-gradient-to-b from-white via-green-50/30 to-white scroll-mt-24" id="faq">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <span className="inline-block bg-green-100 text-green-800 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Got Questions?
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[rgb(2,58,16)] mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-base text-gray-700 font-serif max-w-2xl mx-auto">
            Everything you need to know about joining Kiaan Agrowtech as a farming partner.
          </p>
        </motion.div>

        {/* Accordion — full-width question bars with dropdown answers */}
        <div className="max-w-3xl mx-auto space-y-3">
          {visibleFaqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              index={index}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>

        {/* View More / Show Less toggle */}
        {initialCount < faqs.length && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleToggle}
              className="group inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-[rgb(2,58,16)] to-green-700 text-white font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(2,58,16,0.6)] hover:shadow-[0_16px_40px_-8px_rgba(2,58,16,0.75)] hover:-translate-y-0.5 transition-all duration-300"
            >
              {showAll ? 'Show Less' : `View More Questions (${faqs.length - initialCount})`}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`w-4 h-4 transition-transform duration-300 ${
                  showAll ? 'rotate-180' : 'group-hover:translate-y-0.5'
                }`}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQ;
